/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useRef } from "react";
import {
	FloatingPanelResizer,
	IFloatingPanelResizerPosition,
	RESIZER_POSITION
} from "./FloatingPanelResizer";
import { isFunction, isHTMLElement } from "@adapptio/lib-shared";
import { IUseDragHandlers, useDrag } from "../../hooks/useDrag";
import { MouseOrTouchEvent } from "@adapptio/lib-components";

export interface IFloatingPanelResizeData {
	resizerPosition: IFloatingPanelResizerPosition;
	movedBy: { x: number; y: number };
}

export interface IFloatingPanelResizersProps {
	onResizeStart?: (event: React.MouseEvent<HTMLDivElement>, data: IFloatingPanelResizeData) => void;
	onResize?: (event: MouseOrTouchEvent, data: IFloatingPanelResizeData) => void;
	onResizeEnd?: () => void;
}

export const FloatingPanelResizers: React.FC<IFloatingPanelResizersProps> = ({ onResizeStart, onResize }) => {
	const activeResizerPosition = useRef<IFloatingPanelResizerPosition>();

	const onDragStart = useCallback<IUseDragHandlers["onDragStart"]>(
		(event) => {
			const target = event.target;

			if (!isFunction(onResizeStart)) {
				return;
			}

			if (!isHTMLElement(target) || !target?.dataset?.position) {
				return;
			}

			const resizerPosition = JSON.parse(target?.dataset?.position);

			activeResizerPosition.current = resizerPosition;

			onResizeStart(event, { resizerPosition, movedBy: { x: 0, y: 0 } });
		},
		[ onResizeStart ]
	);

	const onDragMove = useCallback<IUseDragHandlers["onDragMove"]>(
		(event, data) => {
			if (isFunction(onResize)) {
				onResize(event, {
					resizerPosition: activeResizerPosition.current,
					movedBy: { ...data.movedBy }
				});
			}
		},
		[ onResize ]
	);

	const { startDragging } = useDrag(
		{
			onDragStart,
			onDragMove
		},
		{ preventTextSelection: true }
	);

	return (
		<div onMouseDown={startDragging}>
			<FloatingPanelResizer position={RESIZER_POSITION.TOP_LEFT} />
			<FloatingPanelResizer position={RESIZER_POSITION.TOP} />
			<FloatingPanelResizer position={RESIZER_POSITION.TOP_RIGHT} />

			<FloatingPanelResizer position={RESIZER_POSITION.RIGHT} />

			<FloatingPanelResizer position={RESIZER_POSITION.BOTTOM_LEFT} />
			<FloatingPanelResizer position={RESIZER_POSITION.BOTTOM} />
			<FloatingPanelResizer position={RESIZER_POSITION.BOTTOM_RIGHT} />

			<FloatingPanelResizer position={RESIZER_POSITION.LEFT} />
		</div>
	);
};
